/*
 * Time-to-click cumulative curve.
 *
 * Pairs with LureEffectivenessCard on SimsPage. Plots the cumulative
 * share of clicked sims by delivery-to-click delay on a log x-axis, so
 * the dense sub-hour activity is legible alongside the long tail. The
 * steeper the early curve, the faster a lure converts.
 *
 * Live wiring: SEND_LOG rows now carry real sentAt/clickedAt timestamps
 * (SentCampaigns.DateSent / DateFailed), so per-event delay is the genuine
 * (clickedAt - sentAt) in minutes. Clicked events missing a click timestamp
 * are excluded rather than synthesised.
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from 'themes/ThemeContext';
import { motion } from 'motion/react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid } from 'recharts';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';

const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };

const CLICKED_STATUSES = new Set(['clicked', 'clicked_2nd', 'attachment']);

const MIN_DELAY = 1;
const MAX_DELAY = 60 * 24 * 7;

const REFERENCES = [
    { x: 5, label: '5m' },
    { x: 60, label: '1h' },
    { x: 60 * 24, label: '24h' },
];

// API timestamps come back without a zone suffix; the platform stores UTC.
function parseUtcMs(iso) {
    if (!iso) return null;
    const s = String(iso);
    const withZone = /Z$|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
    const ms = Date.parse(withZone);
    return Number.isNaN(ms) ? null : ms;
}

// Real per-event delay in minutes from the send/click timestamps; null when
// the event has no click timestamp to measure against.
function delayForEvent(e) {
    const sent = parseUtcMs(e.sentAt) ?? (e.dateSent ? Date.parse(e.dateSent + 'T00:00:00Z') : null);
    const clicked = parseUtcMs(e.clickedAt);
    if (sent == null || clicked == null) return null;
    const minutes = (clicked - sent) / 60000;
    return Math.max(MIN_DELAY, Math.min(MAX_DELAY, minutes));
}

function formatMinutes(m) {
    if (m < 1) return '<1m';
    if (m < 60) return `${Math.round(m)}m`;
    if (m < 60 * 24) return `${(m / 60).toFixed(m < 600 ? 1 : 0)}h`;
    return `${(m / (60 * 24)).toFixed(m < 60 * 48 ? 1 : 0)}d`;
}

function percentileAt(curve, target) {
    let last = curve[0];
    for (const p of curve) {
        if (p.x >= target) return p.y;
        last = p;
    }
    return last?.y ?? 0;
}

function CurveTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75, boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                By {formatMinutes(p.x)}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary' }}>
                {p.y.toFixed(0)}% had clicked
            </Typography>
        </Box>
    );
}

export default function TimeToClickCard({ events, ttcAgg, strictAgg }) {
    const theme = useTheme();
    const { themeMode } = useThemeContext();
    const isDark = themeMode === 'dark';

    const { curve, total, fastShare, medianMinutes } = useMemo(() => {
        if (Array.isArray(ttcAgg) && (ttcAgg.length || strictAgg)) {
            const bins = [...ttcAgg].sort((a, b) => a.ttcMin - b.ttcMin);
            const nn = bins.reduce((s, b) => s + b.n, 0);
            if (nn === 0) return { curve: [], total: 0, fastShare: 0, medianMinutes: null };
            let cum = 0; const pts = [];
            for (const b of bins) { cum += b.n; pts.push({ x: b.ttcMin, y: (cum / nn) * 100 }); }
            let med = bins[bins.length - 1].ttcMin, c2 = 0;
            for (const b of bins) { c2 += b.n; if (c2 >= nn / 2) { med = b.ttcMin; break; } }
            const fastN = bins.filter((b) => b.ttcMin <= 60).reduce((s, b) => s + b.n, 0);
            return { curve: [{ x: MIN_DELAY, y: 0 }, ...pts], total: nn, fastShare: Math.round((fastN / nn) * 100), medianMinutes: med };
        }
        const clicked = (events || []).filter((e) => CLICKED_STATUSES.has(e.status));
        if (clicked.length === 0) return { curve: [], total: 0, fastShare: 0, medianMinutes: null };

        const delays = clicked.map(delayForEvent).filter((d) => d != null).sort((a, b) => a - b);
        if (delays.length === 0) return { curve: [], total: 0, fastShare: 0, medianMinutes: null };
        const n = delays.length;
        const points = delays.map((d, i) => ({ x: d, y: ((i + 1) / n) * 100 }));
        const withFloor = [{ x: MIN_DELAY, y: 0 }, ...points];

        let median = delays[Math.floor((n - 1) / 2)];
        const fast = (delays.filter((d) => d <= 60).length / n) * 100;
        return { curve: withFloor, total: n, fastShare: Math.round(fast), medianMinutes: median };
    }, [events, ttcAgg, strictAgg]);

    if (total === 0) {
        return (
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                    <Box sx={{ p: 3 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                            Time to click
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 3 }}>
                            Cumulative share of clicks by delivery-to-click delay.
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>No clicked sims in scope.</Typography>
                    </Box>
                </MainCard>
            </motion.div>
        );
    }

    const accent = theme.palette.primary?.main || '#5E72E4';
    const grid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const refLine = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)';
    const tickColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';

    const fiveMin = Math.round(percentileAt(curve, 5));
    const oneHour = Math.round(percentileAt(curve, 60));
    const oneDay = Math.round(percentileAt(curve, 60 * 24));

    return (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Time to click
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                Cumulative share of clicked sims by delivery-to-click delay. Steeper = faster burn.
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                                Median delay
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                                {formatMinutes(medianMinutes)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                        {[
                            { label: 'By 5 min', value: fiveMin },
                            { label: 'By 1 hour', value: oneHour },
                            { label: 'By 24 hours', value: oneDay },
                        ].map((s) => (
                            <Box key={s.label} sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                                    {s.label}
                                </Typography>
                                <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1.1 }}>
                                    <CountUp end={s.value} duration={1.1} />%
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={curve} margin={{ top: 20, right: 20, left: 4, bottom: 4 }}>
                                <defs>
                                    <linearGradient id="ttcFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis
                                    dataKey="x"
                                    type="number"
                                    scale="log"
                                    domain={[MIN_DELAY, MAX_DELAY]}
                                    ticks={[1, 5, 60, 60 * 24, 60 * 24 * 7]}
                                    tickFormatter={formatMinutes}
                                    stroke={tickColor}
                                    tick={{ fontSize: 10, fill: tickColor }}
                                    tickLine={false}
                                    axisLine={false}
                                    allowDataOverflow
                                />
                                <YAxis
                                    domain={[0, 100]}
                                    ticks={[0, 25, 50, 75, 100]}
                                    tickFormatter={(v) => `${v}%`}
                                    stroke={tickColor}
                                    tick={{ fontSize: 10, fill: tickColor }}
                                    tickLine={false}
                                    axisLine={false}
                                    width={40}
                                />
                                <Tooltip content={<CurveTooltip />} cursor={{ stroke: accent, strokeDasharray: '3 3', strokeOpacity: 0.6 }} />
                                {REFERENCES.map((r) => (
                                    <ReferenceLine
                                        key={r.x}
                                        x={r.x}
                                        stroke={refLine}
                                        strokeDasharray="2 4"
                                        label={{ value: r.label, position: 'top', fill: tickColor, fontSize: 9, fontWeight: 700 }}
                                    />
                                ))}
                                <Area
                                    type="monotone"
                                    dataKey="y"
                                    stroke={accent}
                                    strokeWidth={2}
                                    fill="url(#ttcFill)"
                                    isAnimationActive
                                    animationDuration={900}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>

                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 1 }}>
                        n = {total.toLocaleString()} clicked sims · log time axis
                    </Typography>
                </Box>
            </MainCard>
        </motion.div>
    );
}
